import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import { ToWords } from 'to-words';
import { toast } from 'react-toastify';
import {
  getReservationDetailsObject,
} from "utils/utils";
import {
  generateInvoice,
  getBillingInvoiceDetails,
} from "redux/actions/BillingAction";
import CommonButton from "components/commonButton/CommonButton";
import CommonNoDataFound from "components/CommonNoDataFound/CommonNoDataFound";
import Loader from "components/loader/Loader";
import { tripPerformaInvoiceData } from "./BillingInvoiceData";
import { tripDutyTypeEnum } from "utils/enumObj";
import consolidatedInvoiceService from "services/ConsolidatedInvoiceService";

// Helper function to get toll and parking charges
const getTollAndParkingCharges = (tripDetails) => {
  const tollCharges = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(item => item.chargeType === 'tollCharges')?.addedCharge || 0;
  const parkingCharges = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown?.find(item => item.chargeType === 'parkingCharge')?.addedCharge || 0;
  
  if (tollCharges || parkingCharges) {
    return parseFloat(tollCharges) + parseFloat(parkingCharges);
  }
  
  return parseFloat(tripDetails?.tollAndParkingCharges || 0);
};

// Helper function to get garage charges
const getGarageCharges = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];
  const garageCharge = breakdown.find(item => item?.chargeType === 'garageCharge') || {};
  const garageBreakup = garageCharge?.meta?.breakup || [];
  
  const garageToPickup = parseFloat(garageBreakup[0]?.rate || 0);
  const dropToGarage = parseFloat(garageBreakup[1]?.rate || 0);
  
  return garageToPickup + dropToGarage;
};

// Helper function to calculate Sub Total A (base fare + extra charges + surcharges + platform charge)
const calculateSubTotalA = (tripDetails) => {
  const tripPricing = tripDetails?.Payments?.[0]?.tripPricingDetails || {};
  
  const baseRate = parseFloat(tripPricing?.baseRate || 0);
  const surCharge = parseFloat(tripPricing?.surCharge || 0);
  const platformCharge = parseFloat(tripPricing?.platformCharge || 0);
  const extraKmCharges = tripDetails?.type === tripDutyTypeEnum?.rental 
    ? parseFloat(tripDetails?.extraKmCharges || 0)
    : parseFloat(tripDetails?.extraPrice || 0);
  const extraHrCharges = parseFloat(tripDetails?.extraHrCharges || 0);
  const garageCharges = getGarageCharges(tripDetails);
  const baseFare = parseFloat(tripPricing?.baseFareDisplay || 0);

  
  return baseFare + baseRate + surCharge + platformCharge + extraKmCharges + extraHrCharges + garageCharges;
};

// Helper function to calculate Sub Total B (all additional charges)
const calculateSubTotalB = (tripDetails) => {
  const breakdown = tripDetails?.Payments?.[0]?.tripPricingDetails?.breakdown || [];

  const tollAndParking = getTollAndParkingCharges(tripDetails);
  const driverAllowance = parseFloat(breakdown.find(b => b.chargeType === "driverAllowance")?.addedCharge || 0);
  const handlingCharges = parseFloat(tripDetails?.handlingCharges || 0);
  const nightAllowance = parseFloat(breakdown.find(b => b.chargeType === "nightAllowance")?.addedCharge || 0);
  const fuelSurcharge = parseFloat(tripDetails?.fuelSurcharge || 0);
  const interstateTax = parseFloat(tripDetails?.interstateTaxCharges || 0);
  const placardCharge = parseFloat(breakdown.find(b => b.chargeType === "placard")?.addedCharge || 0);
  const additionalCharges = parseFloat(breakdown.find(b => b.chargeType === "additionalCharges")?.addedCharge || 0);
  
  return tollAndParking + driverAllowance + handlingCharges + nightAllowance + fuelSurcharge + interstateTax + placardCharge + additionalCharges;
};

const BillingPerformaInvoice = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toWords = new ToWords();
  const pathName = location?.pathname?.split("/");
  const [searchParams] = useSearchParams();
  const [consolidatedInvoiceLoading, setConsolidatedInvoiceLoading] = useState(false);

  const params = Object.fromEntries([...searchParams]);

  const queryParams = {
    cityId: params?.cityId || "",
    cityName: params?.cityName || "",
    companyId: params?.companyId || "",
    companyName: params?.companyName || "",
    tripId: params?.tripId || "",
    page: params?.page || "",
    pageRowSize: params?.pageRowSize || "",
    status: params?.status || "",
    fromDate: params?.startDate === "undefined" ? "" : params?.startDate ? params?.startDate : "",
    toDate: params?.endDate === "undefined" ? ""  : params?.endDate ? params?.endDate : "",
    magicToken: params?.magicToken || "",
    pageType: params?.pageType || ""
  }
  useEffect(() => {
    if (pathName?.includes("operation")) {
      dispatch(getBillingInvoiceDetails(queryParams?.tripId, "private"));
    } else {
      dispatch(getBillingInvoiceDetails(queryParams?.magicToken, "public"));
    }
  }, []);

  const tripDetails = useSelector(
    (state) => state.getBillingInvoiceDetails?.bilingInvoiceDetails?.data
  );

  const tripDetailsLoader = useSelector(
    (state) => state.getBillingInvoiceDetails?.loading
  );
  const tripTypeDetails = getReservationDetailsObject(tripDetails);
  const tripExtraCharges = tripDetails?.type === tripDutyTypeEnum?.rental ? tripDetails?.extraKmCharges : tripDetails?.extraPrice;

  const subTotalA = calculateSubTotalA(tripDetails);
  const subTotalB = calculateSubTotalB(tripDetails);
  const totalAB = Number(subTotalA) + Number(subTotalB);
  const totalGstAmount = totalAB*.01*(Number(tripDetails?.Payments?.[0]?.igst)+Number(tripDetails?.Payments?.[0]?.cgst)+Number(tripDetails?.Payments?.[0]?.sgst))
  const grossTotal = ((totalAB) + (totalGstAmount)).toFixed(2);
  const tripPerformaInvoiceDetails = tripPerformaInvoiceData(
    tripDetails,
    tripTypeDetails,
    subTotalA,
    subTotalB,
    totalAB,
    totalGstAmount,
    grossTotal
  );
  const tollImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === "Toll");
  const parkingImages =
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === "Parking");

  const interStateImages = 
    tripDetails?.TollAndParkings &&
    tripDetails?.TollAndParkings?.filter((obj) => obj.type === "Interstate-Tax");
  const handleGenerateInvoice = () => {
    if(queryParams?.pageType === "generateInvoice") {
      dispatch(generateInvoice({ tripId: queryParams?.tripId, generateType: "b2bPerforma",handleBack }));
    }else{
      window.open(tripDetails?.Invoices?.[0]?.name?.url, '_blank')
    }
  };

  const generateConsolidatedInvoice = async () => {
    setConsolidatedInvoiceLoading(true);
    try {
      const res = await consolidatedInvoiceService.generateConsolidatedInvoice(tripDetails?.parentTripId || tripDetails?.id);
      if (res?.success && res?.data?.invoice?.url) {
        toast.success(res?.message || "Consolidated invoice generated successfully");
        window.open(res.data.invoice.url, '_blank');
      } else {
        toast.error(res?.message || "Failed to generate consolidated invoice");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to generate consolidated invoice");
      console.error("Consolidated Invoice Error:", error);
    } finally {
      setConsolidatedInvoiceLoading(false);
    }
  };

  const commonGridRender = (arrayData, gridSize) => {
    return arrayData?.map((details, index) => (
      <Grid
        container
        item
        xs={gridSize?.xs}
        sm={gridSize?.sm}
        md={gridSize?.md}
        key={index}
        className="pb20"
      >
        <Grid item xs={7} className="textleft" pl={6}>
          <Typography variant="body2" className= {`"colorBlack" ${details?.bold ? "fw600":"op80"}`}>
            {details?.title}
          </Typography>
        </Grid>
        <Grid item xs={5} className="textLeft">
          <Typography variant="body2" className={`"colorBlack pr10 pl10" ${details?.bold ? "fw600":"fw400"}`}>
            {details?.value}
          </Typography>
        </Grid>
      </Grid>
    ));
  };

  const btnText = () => {
    if(queryParams?.pageType === "generateInvoice") {
      return "Confirm"
    }
    return "Download"
  }

  const handleBack = () => {
    if(queryParams?.pageType) {
      navigate(`/operation/allrides/bookingdetails?id=${queryParams?.tripId}&startDate=${queryParams?.fromDate}&endDate=${queryParams?.toDate}&type=allrides&tab=completed&page=${queryParams?.page}&rowPerPage=${queryParams?.pageRowSize}&search=${queryParams?.search || ""}`);
    }
    else {
      navigate(`/operation/billing/billToCompany?companyId=${queryParams?.companyId}&companyName=${queryParams?.companyName}&cityId=${queryParams?.cityId}&cityName=${queryParams?.cityName}&status=${queryParams?.status}&page=${queryParams?.page}&pageRowSize=${queryParams?.pageRowSize}&startDate=${queryParams?.fromDate}&endDate=${queryParams?.toDate}`)
    }
  }

  return (
    <Grid container className="pt20 br20" justifyContent={"center"}>
      {tripDetailsLoader ? (
        <Loader />
      ) : (
        <Grid item md={11}>
          <Box className="alignStart">
            {pathName?.includes("operation") && (
              <ArrowBackIosRoundedIcon
                className="font24 colorGrey mb20"
                role="presentation"
                onClick={handleBack}
              />
            )}
          </Box>

          <Grid
            container
            justifyContent={"space-between"}
            alignItems={"center"}
          >
           <Grid>
            <Typography className="fw500 pb10" variant="head5" component={"p"}>
              {tripDetails?.DriverProfile?.vendorName || tripDetails?.vendorName || "NA"}
            </Typography>
            <Typography className="pb20" variant="body2" component={"p"}>
              Website: {tripDetails?.vendorEmail || "NA"}&nbsp;&nbsp; Phone:&nbsp;
              {tripDetails?.vendorMobile || "NA"}
            </Typography>
          </Grid>
          {pathName?.includes("operation") &&  <Grid container gap={2} alignItems="center" mb={1} justifyContent="end">
            <CommonButton
              name={btnText()}
              iconType="download"
              iconPlace={queryParams?.pageType !== "generateInvoice" && "back"}
              btnClass="btnMedium btnSecondary btnYellow"
              onClick={handleGenerateInvoice}
              disabled={consolidatedInvoiceLoading}
            />
            {(tripDetails?.parentTripId || tripDetails?.additionalInfo?.tripExtendedAsNewTrip) && (
              <CommonButton
                name="Generate Consolidated Invoice"
                iconType="download"
                iconPlace="back"
                btnClass="btnMedium btnSecondary btnYellow"
                onClick={generateConsolidatedInvoice}
                disabled={consolidatedInvoiceLoading}
              />
             )}
          </Grid>}
          </Grid>

          <Grid className="invoiceBorder br20 mb40">
            <Grid
              container
              className="solidBorderBottomBlack pt20"
              item
              xs={12}
            >
              <Grid item xs={12} pl={6}>
                <Grid md={4}>
                  <Typography
                    variant="head5"
                    component={"p"}
                    className="colorSolidBlack fw500 pb10"
                  >
                  {tripDetails?.DriverProfile?.vendorName  || tripDetails?.vendorName || "NA"}
                  </Typography>
                  <Typography
                    variant="body2"
                    component={"p"}
                    className="colorBlack pb20"
                  >
                    {tripDetails?.vendorAddress || "NA"}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item xs={12} md={6}>
                {tripPerformaInvoiceDetails?.companyGstDetails1?.map(
                  (details, index) => (
                    <Grid container item xs={12} key={index} className="pb20">
                      <Grid
                        item
                        xs={6}
                        lg={5}
                        xl={4}
                        className="textleft"
                        pl={6}
                      >
                        <Typography variant="body2" className="colorBlack op80">
                          {details?.title}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        lg={7}
                        xl={8}
                        className="colorBlack textLeft"
                      >
                        <Typography variant="body2">
                          {details?.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={6}
                alignItems={"flex-start"}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.companyDetails2?.map(
                  (details, index) => (
                    <Grid container item xs={12} key={index} className="pb20">
                      <Grid
                        item
                        xs={6}
                        lg={5}
                        xl={4}
                        className="textleft"
                        pl={6}
                      >
                        <Typography variant="body2" className="colorBlack op80">
                          {details?.title}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        lg={7}
                        xl={8}
                        className="colorBlack textLeft"
                      >
                        <Typography variant="body2">
                          {details?.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>

            <Grid
              container
              className="solidBorderBottomBlack pt20"
              item
              xs={12}
            >
              <Grid container item xs={12} md={6}>
                {tripPerformaInvoiceDetails?.detailsArray1?.map(
                  (details, index) => (
                    <Grid container item xs={12} key={index} className="pb20">
                      <Grid
                        item
                        xs={6}
                        lg={5}
                        xl={4}
                        className="textleft"
                        pl={6}
                      >
                        <Typography variant="body2" className="colorBlack op80">
                          {details?.title}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        lg={7}
                        xl={8}
                        className="colorBlack textLeft"
                      >
                        <Typography variant="body2">
                          {details?.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>

              <Grid
                container
                item
                xs={12}
                md={6}
                alignItems={"flex-start"}
                className="height100 flexColumn"
              >
                {tripPerformaInvoiceDetails?.detailsArray2?.map(
                  (details, index) => (
                    <Grid container item xs={12} key={index} className="pb20">
                      <Grid
                        item
                        xs={6}
                        lg={5}
                        xl={4}
                        className="textleft"
                        pl={6}
                      >
                        <Typography variant="body2" className="colorBlack op80">
                          {details?.title}
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        lg={7}
                        xl={8}
                        className="colorBlack textLeft"
                      >
                        <Typography variant="body2">
                          {details?.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>

            <Grid
              container
              className="solidBorderBottomBlack pt20"
              item
              xs={12}
            >
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray3, {
                  xs: 12,
                  sm: 6,
                  md: 4
                })}
              </Grid>
            </Grid>

            <Grid
              container
              className="solidBorderBottomBlack pt20"
              item
              xs={12}
            >
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4, {
                  xs: 12,
                  sm: 6,
                  md: 4
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt20 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray4Summary, {
                  xs: 12,
                  sm: 6,
                  md: 4
                })}
              </Grid>
            </Grid>

            <Grid
              container
              className="solidBorderBottomBlack pt20"
              item
              xs={12}
            >
              <Grid container item xs={12} md={12}>
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5, {
                  xs: 12,
                  sm: 6,
                  md: 4
                })}
              </Grid>
              <Grid container item xs={12} md={12} className="pt20 solidBorderTopBlack">
                {commonGridRender(tripPerformaInvoiceDetails?.detailsArray5Summary, {
                  xs: 12,
                  sm: 6,
                  md: 4
                })}
              </Grid>
            </Grid>

            <Grid container className="pb20" item xs={12}>
              <Grid item xs={6} md={8} className="pt20">
                <div className="justifyBetween">
                <Typography
                  variant="body1"
                  className="fw500 pb20 colorSolidBlack"
                  pl={6}
                >
                  All Cheques/Draft to be drawn in favor of  {tripDetails?.DriverProfile?.vendorName || "NA"}
                </Typography>
                {tripDetails?.status ==='Cancelled' &&<Typography
                  variant="body1"
                  className="fw500 pb20 colorSolidBlack"
                  pl={6}
                >
                  Cancellation Amount
                </Typography>}
                </div>
                {tripPerformaInvoiceDetails?.detailsArray6 &&
                  tripPerformaInvoiceDetails?.detailsArray6?.map(
                    (routingData, index) => (
                      <Grid container className="pb10" pl={6}>
                        <Typography variant="body3" className="colorSolidBlack">
                          {index + 1}.&nbsp;{routingData}
                        </Typography>
                      </Grid>
                    )
                  )}
                  <Grid item pt={3}
                  pl={6}>
                <Typography
                  variant="body3"
                  className="colorSolidBlack fw600"
                  >
                  Net Payable In Words: {!isNaN(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount) ? toWords.convert(Number(tripDetails?.Payments?.[0]?.tripPricingDetails?.totalAmount), { currency: true }).toUpperCase(): 'Zero'}
                </Typography></Grid>
              </Grid>
              <Grid item xs={6} md={4}>
                {(tripDetails?.Payments?.[0]?.igst !== 0 ? tripPerformaInvoiceDetails?.detailsArray7 : tripPerformaInvoiceDetails?.detailsArray8)?.map(
                  (details, index) => (
                    <Grid container item xs={12} key={index} className="pt20">
                      <Grid item xs={7} className="textleft" pl={6}>
                        <Typography variant="body2" className={`"colorBlack" ${details?.bold ? "fw600":"op80"}`}>
                          {details?.title}
                        </Typography>
                      </Grid>
                      <Grid item xs={5} className={`"colorBlack textLeft"`}>
                        <Typography variant="body2" className={` ${details?.bold ? "fw600":"fw400"}`}>
                          {details?.value}
                        </Typography>
                      </Grid>
                    </Grid>
                  )
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid container className="mb40">
            <Grid
              item
              xs={12}
              sm={6}
              className="height400 bgGrey justifyCenter"
            >
              {tripDetails?.tripRouteImage?.url ? (
                <img
                  src={tripDetails?.tripRouteImage?.url}
                  alt="GoogleImage"
                  className="width100 height100"
                />
              ) : (
                <CommonNoDataFound />
              )}
            </Grid>
            <Grid item xs={12} sm={6} className="p30">
              <Typography className="fw600 pb10">
                Guest Start Location
              </Typography>
              <Typography className="pb30">
                {tripDetails?.sourceAddress || "NA"}
              </Typography>
              <Typography className="fw600 pb10">
                Guest Drop Location
              </Typography>
              <Typography className="pb30">
                {tripTypeDetails?.tripAddress ||
                  tripTypeDetails?.destinationAddress ||
                  "NA"}
              </Typography>
              <Typography className="fw600 pb30">
                Distance Covered:&nbsp;&nbsp;&nbsp;
                {tripDetails?.actualDistance || 0} km
              </Typography>
              <Grid container>
                <Grid item xs={12} sm={6} className="justifyCenter">
                  <Typography className="textCenter colorPrimary fw600">
                    Start Trip OTP Verified
                    <br />
                    {tripDetails?.TripOtp?.startOtp || "NA"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} className="justifyCenter">
                  <Typography className="textCenter colorPrimary fw600">
                    End Trip OTP Verified
                    <br />
                    {tripDetails?.TripOtp?.endOtp || "NA"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* TODO: required an data from backend */}
          {tripDetails?.dutyTypeName === "Rental" && tripDetails?.RoutingSummaries.length > 0 &&
          <Grid className="mb40">
            <Typography variant="head5" className="fw600 pb30">
              Routing Summary Form Pick-Up To Drop
            </Typography>
            <Grid container className="solidBorderBottomBlack pb20 mt20">
              <Grid xs={12} sm={1} className="justifyCenter">
                <Typography variant="head6" className="textCenter">
                  Sr. No.
                </Typography>
              </Grid>
              <Grid xs={12} sm={11}>
                <Typography variant="head6">Location</Typography>
              </Grid>
            </Grid>
            {tripDetails?.RoutingSummaries &&
              tripDetails?.RoutingSummaries?.map(
                (routingData, index) => (
                  <Grid container className="ptb20 dashedBorderBottom">
                    <Grid xs={12} sm={1} className="justifyCenter">
                      <Typography
                        variant="body3"
                        className="colorGrey textCenter"
                      >
                        {index + 1}
                      </Typography>
                    </Grid>
                    <Grid xs={12} sm={11}>
                      <Typography variant="body3" className="colorGrey">
                        {routingData?.address}
                      </Typography>
                    </Grid>
                  </Grid>
                )
              )}
          </Grid>}

          <Grid container className="mb30" gap={3} flexWrap={"nowrap"}>
            {tollImages?.[0]?.TollAndParkingReceipts &&
              tollImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
                <Grid items xs={12} sm={6} md={3}>
                  <img
                    src={tollImages?.url}
                    alt="Toll and parking"
                    className="width100 height200 br5"
                  />
                </Grid>
              ))}
          </Grid>

          <Grid container className="mb40" gap={3} flexWrap={"nowrap"}>
            {parkingImages?.[0]?.TollAndParkingReceipts &&
              parkingImages?.[0]?.TollAndParkingReceipts?.map(
                (parkingImages) => (
                  <Grid items xs={12} sm={6} md={3}>
                    <img
                      src={parkingImages?.url}
                      alt="Toll and parking"
                      className="width100 height200 br5"
                    />
                  </Grid>
                )
              )}
          </Grid>
          <Grid container className="mb30" gap={3} flexWrap={"nowrap"}>
          {interStateImages?.[0]?.TollAndParkingReceipts &&
            interStateImages?.[0]?.TollAndParkingReceipts?.map((tollImages) => (
              <Grid items xs={12} sm={6} md={3}>
                <img
                  src={tollImages?.url}
                  alt="InterState-Rate"
                  className="width100 height200 br5"
                />
              </Grid>
            ))}
        </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default BillingPerformaInvoice;
